import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Video, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const About = () => {
  const venues = [
    { name: 'Le Domaine IMODIS', url: 'https://www.imodis.fr' },
    { name: 'La Villa Quai Sturm', url: 'https://villa-quai-sturm.fr', location: 'Strasbourg' },
    { name: 'Le Château de Pourtalès', url: 'https://www.destination-pourtales.fr' },
    { name: 'Le Domaine du Moulin', url: 'https://www.hotel-domainedumoulin-alsace.com/fr/', location: 'Ensisheim' },
    { name: "L'Orangerie du Manoir", url: '' },
    { name: 'Le Château de Thanvillé', url: 'https://www.chateau-de-thanville.com' },
    { name: 'Le Jardin du Nideck', url: 'https://www.lesjardinsdunideck.fr' },
    { name: 'Le Clos des Sources', url: 'https://www.leclosdessources.com', location: 'Thannenkirch' },
    { name: 'La Ferme de Marie Hélène', url: 'https://www.fermemariehelene.com' },
    { name: 'Du Côté de Chez Anne', url: 'https://www.du-cote-de-chez-anne.com/' },
    { name: "L'Abbaye des Prémontrés", url: 'https://www.abbaye-premontres.com', location: 'Pont-à-Mousson' },
    { name: 'Le Domaine du Hirtz', url: 'https://www.domaine-hirtz.com' },
    { name: 'Le Domaine de Raville', url: 'https://domainederaville.fr' },
    { name: 'La Grange de Condé', url: 'https://www.lagrangedeconde.com', location: 'Lorraine' },
    { name: 'Le Château Hochberg', url: 'https://chateauhochberg.com' },
    { name: "La Drille au Bord de l'Eau", url: 'http://mariage-atypique.fr' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Yacine - Vidéaste Mariage Professionnel en Alsace | À Propos - Yastudio"
        description="Découvrez Yacine, vidéaste mariage professionnel en Alsace. Spécialisé dans les films de mariage à Strasbourg, Mulhouse, Colmar et toute la région Grand Est."
        keywords="vidéaste mariage Alsace, vidéaste professionnel Strasbourg, film mariage Alsace, reportage vidéo mariage Grand Est"
      />
      {/* Hero Section */}
      <section className="py-32 px-4 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Qui suis-je ?
          </h1>
          <p className="text-xl md:text-2xl text-gray-300">
            Un vidéaste humain, discret et à l'écoute de vos émotions
          </p>
        </div>
      </section>

      {/* About Me Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Je m'appelle <span className="text-light-pink">Yacine</span>
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Vidéaste professionnel en Alsace spécialisé dans les événements privés : mariages, 
                anniversaires, baptêmes, et tous ces moments de vie qui méritent d'être racontés 
                avec justesse et émotion.
              </p>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Mon approche est avant tout humaine et sensible. Le jour de votre mariage, je me 
                fais discret, pour mieux capturer la spontanéité, les regards complices, les rires 
                sincères et les instants forts qui se vivent souvent sans mise en scène.
              </p>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Je privilégie la prise de vue naturelle, dans le respect de votre intimité, pour 
                que votre film soit fidèle à votre histoire, à vos émotions, à votre énergie.
              </p>
            </div>
            <div className="order-1 md:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_alsace-wedding-pro/artifacts/bsk4uaer_PHOTO-2025-08-17-20-19-46.jpeg"
                alt="Yacine - Vidéaste professionnel mariage Alsace"
                className="rounded-lg shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Ma Philosophie
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-light-pink rounded-full flex items-center justify-center mb-6 mx-auto">
                <Heart className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Approche Humaine</h3>
              <p className="text-gray-400 leading-relaxed">
                Une écoute attentive de vos désirs et une présence discrète pour capturer 
                l'authenticité de vos émotions.
              </p>
            </div>

            <div className="bg-black p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-mid-blue rounded-full flex items-center justify-center mb-6 mx-auto">
                <Video className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Qualité Professionnelle</h3>
              <p className="text-gray-400 leading-relaxed">
                Un matériel haut de gamme et une expertise technique pour un rendu 
                cinématographique exceptionnel.
              </p>
            </div>

            <div className="bg-black p-8 rounded-lg text-center">
              <div className="w-16 h-16 bg-mid-purple rounded-full flex items-center justify-center mb-6 mx-auto">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Relation de Confiance</h3>
              <p className="text-gray-400 leading-relaxed">
                Un rendez-vous préalable pour apprendre à nous connaître et créer une 
                relation de confiance avant le jour J.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="bg-zinc-900 p-10 md:p-16 rounded-2xl border border-zinc-800">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Mon Engagement
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
              Chaque couple, chaque famille, chaque événement est unique. C'est pourquoi je propose 
              toujours un rendez-vous avant le jour J, en visio ou en présentiel, pour apprendre à 
              nous connaître.
            </p>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
              Ce moment d'échange me permet de comprendre vos envies, vos personnalités, votre 
              rythme… et de vous mettre à l'aise avec ma présence le jour venu. Ensemble, nous 
              préparons le terrain pour que vous puissiez vivre votre mariage pleinement, en toute 
              confiance.
            </p>
            <p className="text-lg md:text-xl text-white font-semibold leading-relaxed text-center">
              Mon objectif : raconter votre histoire avec authenticité, élégance et émotion, en 
              créant un souvenir intemporel que vous aurez plaisir à revoir, à partager, à transmettre.
            </p>
          </div>
        </div>
      </section>

      {/* Venues Section */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-mid-blue rounded-full flex items-center justify-center mb-6 mx-auto">
              <MapPin className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Vidéaste dans les Plus Beaux Domaines
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              d'Alsace et du Grand Est
            </p>
          </div>

          <p className="text-lg text-gray-300 mb-12 text-center max-w-4xl mx-auto leading-relaxed">
            Vous vous mariez dans un lieu d'exception ? J'ai eu le privilège de filmer dans les 
            plus belles salles de réception de la région. Que vous choisissiez un château élégant, 
            un domaine au cœur de la nature, ou une salle de réception contemporaine, je m'adapte 
            à chaque lieu pour en tirer le meilleur.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {venues.map((venue, index) => (
              <div 
                key={index}
                className="bg-black p-6 rounded-lg border border-zinc-800 hover:border-light-pink transition-all duration-300 hover:transform hover:scale-105"
              >
                {venue.url ? (
                  <a 
                    href={venue.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <h3 className="text-lg font-semibold mb-2 text-white hover:text-light-pink transition-colors">
                      {venue.name}
                    </h3>
                    {venue.location && (
                      <p className="text-sm text-gray-500">{venue.location}</p>
                    )}
                  </a>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold mb-2 text-white">
                      {venue.name}
                    </h3>
                    {venue.location && (
                      <p className="text-sm text-gray-500">{venue.location}</p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          <p className="text-lg text-gray-300 mt-12 text-center italic">
            Et bien d'autres lieux magnifiques dans toute la région Grand Est...
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-black to-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à Créer Votre Film de Mariage ?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Rencontrons-nous pour discuter de votre projet
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-light-pink text-black hover:bg-light-pink/90 text-lg px-12 py-6 rounded-full">
              Prendre Contact
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;